import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ProcessedCertificate, COURSE_CONFIG } from '../types';
import { supabaseReviewStorage } from '../services/supabaseStorage';
import { generateCertificateHTML } from './certificateHtmlTemplate';
import { applyCertificateSvgTextLayout } from './certificateSvgTextLayout';
import { computeExpiryIsoFromCourseDate } from './certificatePlaceholders';
import { CertificateSettingsService } from '../services/certificateSettingsService';

// Certificate generation method selector
// Can be set via environment variable: VITE_CERTIFICATE_METHOD=html or VITE_CERTIFICATE_METHOD=jspdf
// Or via superadmin settings (takes precedence)
// Or change the default value below directly
let cachedMethod: 'html' | 'jspdf' | null = null;
let methodPromise: Promise<'html' | 'jspdf'> | null = null;

const getCertificateMethod = async (): Promise<'html' | 'jspdf'> => {
  // Force HTML renderer always.
  return 'html';
};

// Function to clear cache (call this when settings are updated)
export const clearCertificateMethodCache = () => {
  cachedMethod = null;
  methodPromise = null;
};

// Professional certificate dimensions for A4 format
const CERTIFICATE_WIDTH = 794; // px
const CERTIFICATE_HEIGHT = 1123; // px

// Color palette
const COLORS = {
  WHITE: '#FFFFFF',
  BLACK: '#000020',
  BRAND_GREEN: '#00923f',
  RED: '#FF0000',
  GRAY: '#666666'
};

const getCertificateNumber = async (): Promise<string> => {
  try {
    const counter = await supabaseReviewStorage.incrementCertificateCounter();
    return `TT-${counter.toString().padStart(6, '0')}`;
  } catch (error) {
    console.error('Error getting certificate number:', error);
    // Fallback to a timestamp-based number if database fails
    const timestamp = Date.now().toString().slice(-6);
    return `TT-${timestamp}`;
  }
};

// Helper function to resolve image path for production
const resolveImagePath = (imagePath: string): string => {
  // If path already has protocol, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Get the base URL from window.location
  const baseUrl = window.location.origin;

  // Ensure path starts with /
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

  return `${baseUrl}${normalizedPath}`;
};

// Helper function to load and add images to PDF
const addImageToPDF = async (doc: jsPDF, imageSrc: string, x: number, y: number, width: number, height: number): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL('image/png');
        doc.addImage(dataURL, 'PNG', x, y, width, height);
        resolve();
      } catch (error) {
        console.warn('Failed to add image:', error);
        resolve(); // Continue without image
      }
    };
    img.onerror = (error) => {
      console.warn('Failed to load image:', imageSrc, error);
      resolve(); // Continue without image
    };
    // Use resolved path for production compatibility
    img.src = resolveImagePath(imageSrc);
  });
};

// Calculate expiry date based on course validity (course date + months − 1 day)
export const calculateExpiryDate = (courseDate: string, courseName: string): string => {
  const courseConfig = COURSE_CONFIG[courseName as keyof typeof COURSE_CONFIG];
  const validityMonths = courseConfig?.validityMonths || 36;
  return computeExpiryIsoFromCourseDate(courseDate, validityMonths);
};

// Format date for display  
export const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short', 
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Add dotted line under text
const addDottedLine = (doc: jsPDF, x: number, y: number, width: number): void => {
  doc.setLineWidth(0.2);
  doc.setDrawColor(102, 102, 102); // Gray
  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(x, y, x + width, y);
  doc.setLineDashPattern([], 0); // Reset dash pattern
};

// Split long text into multiple lines that fit within specified width
const splitTextToFit = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const textWidth = doc.getStringUnitWidth(testLine) * doc.getFontSize() / doc.internal.scaleFactor;
    
    if (textWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Word is too long, force it on its own line
        lines.push(word);
      }
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines;
};

// Format ordinal numbers (1st, 2nd, 3rd, 4th, etc.)
const getOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Parse date from various formats
const parseDate = (dateStr: string): Date => {
  const trimmed = dateStr.trim();
  
  if (trimmed.includes('/') || trimmed.includes('-') || trimmed.includes('.')) {
    const separator = trimmed.includes('/') ? '/' : trimmed.includes('-') ? '-' : '.';
    const parts = trimmed.split(separator);
    if (parts.length === 3) {
      const part1 = parseInt(parts[0]);
      const part2 = parseInt(parts[1]);
      const part3 = parseInt(parts[2]);
      
      if (parts[0].length === 4 && part1 > 1900) {
        // YYYY-MM-DD format
        return new Date(part1, part2 - 1, part3, 12, 0, 0);
      } else if (parts[2].length === 4 && part3 > 1900) {
        // DD-MM-YYYY format
        return new Date(part3, part2 - 1, part1, 12, 0, 0);
      } else {
        // Try both formats
        if (part1 <= 31 && part2 <= 12 && part3 > 1900) {
          return new Date(part3, part2 - 1, part1, 12, 0, 0);
        } else if (part1 > 1900 && part2 <= 12 && part3 <= 31) {
          return new Date(part1, part2 - 1, part3, 12, 0, 0);
        }
      }
    }
  } else if (trimmed.length === 8 && /^\d+$/.test(trimmed)) {
    // DDMMYYYY format
    const day = parseInt(trimmed.substring(0, 2));
    const month = parseInt(trimmed.substring(2, 4)) - 1;
    const year = parseInt(trimmed.substring(4, 8));
    return new Date(year, month, day, 12, 0, 0);
  }
  
  // Fallback to Date constructor
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) {
    throw new Error(`Unable to parse date: ${dateStr}`);
  }
  return date;
};

// Format multiple dates as "1st, 2nd & 3rd of September 2025"
const formatMultipleDates = (dates: string[]): string => {
  if (dates.length === 0) return '';
  if (dates.length === 1) {
    // Single date - format normally
    try {
      const date = parseDate(dates[0]);
      const day = getOrdinal(date.getDate());
      const month = date.toLocaleDateString('en-GB', { month: 'long' });
      const year = date.getFullYear();
      return `${day} of ${month} ${year}`;
    } catch {
      return dates[0];
    }
  }

  // Multiple dates - parse and group by month/year
  const parsedDates = dates
    .map(dateStr => {
      try {
        return parseDate(dateStr);
      } catch {
        return null;
      }
    })
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  if (parsedDates.length === 0) return dates.join(', ');

  // Group by month/year
  const groups: { month: string; year: number; days: number[] }[] = [];
  parsedDates.forEach(date => {
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    const day = date.getDate();

    const existingGroup = groups.find(g => g.month === month && g.year === year);
    if (existingGroup) {
      existingGroup.days.push(day);
    } else {
      groups.push({ month, year, days: [day] });
    }
  });

  // Format each group
  return groups
    .map(group => {
      const sortedDays = [...group.days].sort((a, b) => a - b);
      const formattedDays = sortedDays.map(day => getOrdinal(day));
      
      if (formattedDays.length === 1) {
        return `${formattedDays[0]} of ${group.month} ${group.year}`;
      } else if (formattedDays.length === 2) {
        return `${formattedDays[0]} & ${formattedDays[1]} of ${group.month} ${group.year}`;
      } else {
        const last = formattedDays.pop();
        return `${formattedDays.join(', ')} & ${last} of ${group.month} ${group.year}`;
      }
    })
    .join(', ');
};

// Generate PDF using HTML-to-PDF method (hidden div + DOMParser, same as reference template)
const generateCertificatePDFHTML = async (certificate: ProcessedCertificate): Promise<Blob> => {
  let mount: HTMLDivElement | null = null;
  const stylesheetLinks: HTMLLinkElement[] = [];

  try {
    const html = await generateCertificateHTML(certificate);

    mount = document.createElement('div');
    mount.setAttribute('data-certificate-capture', '1');
    // Keep on-screen (opacity 0). Off-screen (-200vw) breaks html2canvas clone lookup.
    mount.style.position = 'fixed';
    mount.style.left = '0';
    mount.style.top = '0';
    mount.style.width = '794px';
    mount.style.height = '1123px';
    mount.style.margin = '0';
    mount.style.padding = '0';
    mount.style.overflow = 'hidden';
    mount.style.background = '#fff';
    mount.style.opacity = '0';
    mount.style.pointerEvents = 'none';
    mount.style.zIndex = '-1';

    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, 'text/html');
    const hasFullDoc =
      /<html[\s>]/i.test(html) ||
      /<body[\s>]/i.test(html) ||
      /<!doctype/i.test(html);

    if (hasFullDoc) {
      const styleMarkup = Array.from(parsed.head.querySelectorAll('style'))
        .map((node) => node.outerHTML)
        .join('\n');
      // Neutralize body padding from the template so capture is edge-to-edge A4.
      mount.innerHTML = `${styleMarkup}<style>body,html{padding:0!important;margin:0!important;background:#fff!important}</style>${parsed.body.innerHTML}`;

      Array.from(parsed.head.querySelectorAll('link[rel="stylesheet"]')).forEach((node) => {
        const src = node as HTMLLinkElement;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = src.href;
        document.head.appendChild(link);
        stylesheetLinks.push(link);
      });
    } else {
      mount.innerHTML = html;
    }

    document.body.appendChild(mount);

    await Promise.all(
      stylesheetLinks.map(
        (link) =>
          new Promise<void>((resolve) => {
            if (link.sheet) {
              resolve();
              return;
            }
            const done = () => resolve();
            link.addEventListener('load', done, { once: true });
            link.addEventListener('error', done, { once: true });
            setTimeout(done, 8000);
          })
      )
    );

    const target = (mount.querySelector('.page') ||
      mount.querySelector('.certificate-page') ||
      mount.querySelector('svg')) as HTMLElement | null;
    if (!target) {
      throw new Error('Certificate HTML did not render (missing .page root).');
    }

    target.style.width = '794px';
    target.style.height = '1123px';
    target.style.margin = '0';
    target.style.boxShadow = 'none';

    const images = mount.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            const timeout = setTimeout(() => resolve(), 5000);
            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = () => {
              clearTimeout(timeout);
              resolve();
            };
          })
      )
    );

    const qrImg = mount.querySelector('img.cert-qr') as HTMLImageElement | null;
    if (qrImg && (!qrImg.getAttribute('src') || qrImg.naturalWidth === 0)) {
      console.warn('Certificate QR image missing or failed to load');
    }

    await new Promise((r) => setTimeout(r, 200));
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* ignore */
      }
    }

    // innerHTML does not run <script>; apply wrap/fit synchronously before capture.
    // Temporarily make the mount measurable — opacity:0 can yield 0 text lengths.
    const prevOpacity = mount.style.opacity;
    mount.style.opacity = '1';
    applyCertificateSvgTextLayout(mount);
    mount.style.opacity = prevOpacity;

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      height: 1123,
      windowWidth: 794,
      windowHeight: 1123,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      onclone: (_doc, el) => {
        const node = el as HTMLElement;
        node.style.opacity = '1';
        node.style.transform = 'none';
        node.style.left = '0';
        node.style.top = '0';
      },
    });

    const imageData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    pdf.addImage(imageData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');

    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating PDF with HTML method:', error);
    throw new Error(
      `Failed to generate PDF for ${certificate.attendeeName}: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  } finally {
    stylesheetLinks.forEach((link) => link.remove());
    if (mount?.parentNode) {
      document.body.removeChild(mount);
    }
    mount = null;
  }
};

// Main PDF generation function - routes to appropriate method
export const generateCertificatePDF = async (certificate: ProcessedCertificate): Promise<Blob> => {
  // Force HTML renderer only (no jsPDF fallback).
  await new Promise(resolve => setTimeout(resolve, 500));
  return generateCertificatePDFHTML(certificate);
};

// Rename existing function to generateCertificatePDFJSPDF
const generateCertificatePDFJSPDF = async (certificate: ProcessedCertificate): Promise<Blob> => {
  let formattedExpiryDate: string;
  
  // Calculate expiry date first before any PDF generation
  try {
    // Get course configuration
    const courseConfig = COURSE_CONFIG[certificate.courseName as keyof typeof COURSE_CONFIG];
    const validityMonths = courseConfig?.validityMonths || 36; // Default to 36 months if config missing
    
    // For multi-day bookings, use the last date for expiry (any course)
    const hasMultipleDates = certificate.courseDates && certificate.courseDates.length > 0;
    
    let dateStr: string;
    if (hasMultipleDates) {
      dateStr = certificate.courseDates[certificate.courseDates.length - 1].toString().trim();
    } else {
      dateStr = certificate.courseDate.toString().trim();
    }
    
    // Parse the course date - expect Irish DD-MM-YYYY format
    let courseDate: Date;
    
    // Enhanced date format detection for expiry date calculation
    if (dateStr.includes('-') || dateStr.includes('/') || dateStr.includes('.')) {
      const separator = dateStr.includes('/') ? '/' : dateStr.includes('-') ? '-' : '.';
      const parts = dateStr.split(separator);
      if (parts.length === 3) {
        // Detect format based on part lengths and values
        const part1 = parseInt(parts[0]);
        const part2 = parseInt(parts[1]);
        const part3 = parseInt(parts[2]);
        
        if (parts[0].length === 4 && part1 > 1900) {
          // YYYY-MM-DD format (from database/form)
          courseDate = new Date(part1, part2 - 1, part3, 12, 0, 0);
        } else if (parts[2].length === 4 && part3 > 1900) {
          // DD-MM-YYYY format (Irish standard)
          courseDate = new Date(part3, part2 - 1, part1, 12, 0, 0);
        } else {
          // Fallback: try both formats
          if (part1 <= 31 && part2 <= 12 && part3 > 1900) {
            // Looks like DD-MM-YYYY
            courseDate = new Date(part3, part2 - 1, part1, 12, 0, 0);
          } else if (part1 > 1900 && part2 <= 12 && part3 <= 31) {
            // Looks like YYYY-MM-DD
            courseDate = new Date(part1, part2 - 1, part3, 12, 0, 0);
          } else {
            throw new Error('Ambiguous date format');
          }
        }
      } else {
        throw new Error('Invalid date format');
      }
    } else if (dateStr.length === 8 && /^\d+$/.test(dateStr)) {
      // Handle DDMMYYYY format
      const day = parseInt(dateStr.substring(0, 2));
      const month = parseInt(dateStr.substring(2, 4)) - 1;
      const year = parseInt(dateStr.substring(4, 8));
      courseDate = new Date(year, month, day, 12, 0, 0);
    } else if (dateStr.split('-')[0].length === 4) {
      // Fallback: YYYY-MM-DD format (convert to Irish)
      const parts = dateStr.split('-');
      courseDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
    } else {
      // Last resort: try to parse as-is
      courseDate = new Date(dateStr);
      if (isNaN(courseDate.getTime())) {
        throw new Error('Unable to parse date');
      }
    }
    
    // Validate the parsed date
    if (isNaN(courseDate.getTime())) {
      throw new Error('Invalid date provided');
    }
    
    // Calculate expiry date by adding validity months
    const expiryDate = new Date(courseDate);
    expiryDate.setMonth(courseDate.getMonth() + validityMonths);
    
    // Format for display in Irish format with validation
    if (isNaN(expiryDate.getTime())) {
      throw new Error('Invalid expiry date calculated');
    }
    
    formattedExpiryDate = expiryDate.toLocaleDateString('en-IE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
  } catch (error) {
    console.error('Error calculating expiry date for Irish format:', error);
    
    // Emergency fallback: use today + validity months (but this should rarely happen)
    const courseConfig = COURSE_CONFIG[certificate.courseName as keyof typeof COURSE_CONFIG];
    const validityMonths = courseConfig?.validityMonths || 36;
    
    const today = new Date();
    const fallbackExpiry = new Date(today);
    fallbackExpiry.setMonth(today.getMonth() + validityMonths);
    
    formattedExpiryDate = fallbackExpiry.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }
  
  try {
    const courseConfig = COURSE_CONFIG[certificate.courseName as keyof typeof COURSE_CONFIG];
    if (!courseConfig) {
      throw new Error(`Course configuration not found for: ${certificate.courseName}`);
    }

    // Create PDF in A4 portrait format
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm

    // Define content boundaries early for layout calculations
    const contentBottom = pageHeight - 40; // Bottom margin for footer
    const bottomSectionY = pageHeight - 50; // Position for bottom section elements
    
    // 1. Create container with white background and double borders
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Outer black border (thicker)
    doc.setLineWidth(1.0);
    doc.setDrawColor(0, 0, 32); // Dark blue/black
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
    
    // Inner black border (thinner)
    doc.setLineWidth(0.3);
    doc.rect(12, 12, pageWidth - 24, pageHeight - 24);

    // Define content margins (within inner border)
    const marginLeft = 15;
    const marginRight = pageWidth - 15;
    const contentWidth = marginRight - marginLeft;

    // 2. Header Section - Logo area
    let currentY = 25;
    try {
      // Center the logo horizontally
      const logoWidth = 60;
      const logoX = (pageWidth - logoWidth) / 2;
      await addImageToPDF(doc, 'https://irelandtt.com/img/Irelandtt-logo-transparent.png', logoX, currentY, logoWidth, 20);
    } catch (error) {
      // Professional fallback with better styling
      doc.setFontSize(10);
      doc.setTextColor(0, 146, 63); // Brand green
      doc.setFont('helvetica', 'bold'); // Use helvetica for general text
      doc.text('Ireland T&T', pageWidth / 2, currentY + 8, { align: 'center' });
      doc.text('SOLUTIONS LTD', pageWidth / 2, currentY + 14, { align: 'center' });
    }

    // 3. Title Section - Course name and certificate type in serif font
    currentY = 55;
    doc.setFontSize(20); // Slightly smaller default for better fitting
    doc.setTextColor(0, 0, 32); // Primary text color
    doc.setFont('helvetica', 'bold'); // Modern sans-serif for professional look

    // Special handling for Manual Handling certificate
    let titleLines: string[];
    if (certificate.courseName === 'Manual Handling') {
      // Split into two lines with different font sizes
      doc.setFontSize(24); // Larger for "MANUAL HANDLING"
      doc.text('MANUAL HANDLING', pageWidth / 2, currentY, { align: 'center' });
      currentY += 12;

      doc.setFontSize(16); // Smaller for "CERTIFICATE OF ATTENDANCE"
      doc.text('CERTIFICATE OF ATTENDANCE', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else if (certificate.courseName === 'MEWP Operator') {
      // Special handling for MEWP - single line title
      doc.setFontSize(18);
      doc.text('MEWP OPERATOR', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      doc.setFontSize(16);
      doc.text('CERTIFICATE OF ATTENDANCE', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else if (certificate.courseName === 'MEWP – Spider Lift Operator') {
      // MEWP Spider Lift - two line title
      doc.setFontSize(16);
      doc.text('MEWP – SPIDER LIFT OPERATOR', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      doc.setFontSize(16);
      doc.text('CERTIFICATE OF ATTENDANCE', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else if (certificate.courseName === 'Hot Works & Fire Watch') {
      // Special handling for Hot Works & Fire Watch - split into multiple lines
      doc.setFontSize(18); // Smaller font size to fit better
      doc.text('HOT WORKS & FIRE WATCH', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      doc.setFontSize(16); // Smaller for "CERTIFICATE OF ATTENDANCE"
      doc.text('CERTIFICATE OF ATTENDANCE', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else if (certificate.courseName === 'Fire Safety and Extinguisher Use') {
      // Special handling for Fire Safety - split into multiple lines with smaller font
      doc.setFontSize(16); // Smaller for "CERTIFICATE OF ATTENDANCE"
      doc.text('CERTIFICATE OF ATTENDANCE', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      doc.setFontSize(14); // Smaller font for long title
      doc.text('FIRE SAFETY AWARENESS &', pageWidth / 2, currentY, { align: 'center' });
      currentY += 8;

      doc.text('USE OF PORTABLE FIRE EXTINGUISHERS', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else if (certificate.courseName === 'Safety Awareness Training for Shunter & Signalers') {
      // Special handling for Shunter & Signalers - split into multiple lines with smaller font
      doc.setFontSize(14); // Smaller font for long title
      const titleText = 'CERTIFICATE OF SAFETY AWARENESS TRAINING FOR';
      doc.text(titleText, pageWidth / 2, currentY, { align: 'center' });
      currentY += 8;
      
      doc.setFontSize(14);
      doc.text('SHUNTER & SIGNALERS', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      titleLines = []; // Already handled above
    } else {
      titleLines = courseConfig.displayNameOnCertificate.split('\n');
      titleLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, currentY + (index * 10), { align: 'center' });
      });
      currentY += (titleLines.length * 10) + 15;
    }

    // 4. Certification Statement
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 32);
    doc.setFont('helvetica', 'normal'); // Inter equivalent
    doc.text('This is to certify that', pageWidth / 2, currentY, { align: 'center' });
    currentY += 12;

    // 5. Recipient's Name - Large, green, script-style font
    doc.setFontSize(26);
    doc.setFont('times', 'italic'); // Times italic for attendee name
    doc.setTextColor(0, 146, 63); // Brand green
    
    // Format name with proper case (first letter capital, rest lowercase)
    const formattedName = certificate.attendeeName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    doc.text(formattedName, pageWidth / 2, currentY, { align: 'center' });
    currentY += 6;

    // Add course-specific image under attendee name (skip for MEWP)
    const courseNameLower = certificate.courseName.toLowerCase();
    const isMEWPCourse = certificate.courseName === 'MEWP Operator' || certificate.courseName === 'MEWP – Spider Lift Operator';
    const isCounterbalanceCourse = courseNameLower.includes('counterbalance');
    const isStackerCourse = courseNameLower.includes('stacker');
    const isMultiReachCourse = courseNameLower.includes('multidirectional');
    const isReachCourse = courseNameLower.includes('reach') && !isMultiReachCourse;
    const isVNACourse = courseNameLower.includes('vna');
    const isOrderPickerCourse = courseNameLower.includes('order picker');
    const isPivotSteerCourse = courseNameLower.includes('pivot steer');
    const isPPTCourse = courseNameLower.includes('ppt') || courseNameLower.includes('power pallet');
    
    // Define hasLargeImage for use throughout the function
    const hasLargeImage = isReachCourse || isVNACourse || isMultiReachCourse || isStackerCourse || isOrderPickerCourse || isPivotSteerCourse;

    // Skip image rendering for MEWP certificates
    if (!isMEWPCourse) {
      let imagePath: string;
      let imgWidth: number;
      let imgHeight: number;

      if (isCounterbalanceCourse) {
        imagePath = '/images/cb.png';
        imgWidth = 40;
        imgHeight = 28;
      } else if (isPivotSteerCourse) {
        imagePath = '/images/multireach.png';
        imgWidth = 30;
        imgHeight = 35;
      } else if (isMultiReachCourse) {
        imagePath = '/images/multireach.png';
        imgWidth = 30;
        imgHeight = 35;
      } else if (isVNACourse) {
        imagePath = '/images/vna.png';
        imgWidth = 30;
        imgHeight = 35;
      } else if (isReachCourse) {
        imagePath = '/images/reach.png';
        imgWidth = 30;
        imgHeight = 35;
      } else if (isOrderPickerCourse) {
        imagePath = '/images/orderpicker.png';
        imgWidth = 40;
        imgHeight = 28;
      } else if (isStackerCourse) {
        imagePath = '/images/stacker.png';
        imgWidth = 30;
        imgHeight = 35;
      } else {
        // PPT and other courses use horizontal dimensions
        imagePath = '/images/ppt.png';
        imgWidth = 40;
        imgHeight = 28;
      }

      try {
        const imgX = (pageWidth - imgWidth) / 2;
        await addImageToPDF(doc, imagePath, imgX, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 6;
      } catch (error) {
        console.warn(`Failed to add course image (${imagePath}):`, error);
        currentY += 8;
      }
    } else {
      // Add some spacing for MEWP courses where image is skipped
      currentY += 8;
    }

    // 6. Course Completion Statement: prefix (e.g. "HAS SUCCESSFULLY COMPLETED ") in black, course name in brand green
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const maxCompletionWidth = contentWidth - 20;

    const splitCompletionStatement = (stmt: string): { prefix: string; coursePart: string } => {
      const completedIdx = stmt.indexOf('COMPLETED ');
      const attendedIdx = stmt.indexOf('ATTENDED ');
      if (completedIdx !== -1) {
        return { prefix: stmt.slice(0, completedIdx + 'COMPLETED '.length), coursePart: stmt.slice(completedIdx + 'COMPLETED '.length).trim() };
      }
      if (attendedIdx !== -1) {
        return { prefix: stmt.slice(0, attendedIdx + 'ATTENDED '.length), coursePart: stmt.slice(attendedIdx + 'ATTENDED '.length).trim() };
      }
      return { prefix: '', coursePart: stmt };
    };

    const isShunterSignalers = certificate.courseName === 'Safety Awareness Training for Shunter & Signalers';
    const rawStatement = isShunterSignalers ? `${courseConfig.completionStatement} ${certificate.companyName} on` : courseConfig.completionStatement;
    const { prefix, coursePart } = splitCompletionStatement(rawStatement);

    const isAbrasiveWheels = certificate.courseName === 'Abrasive Wheels';
    if (prefix) {
      doc.setTextColor(0, 0, 32);
      const prefixLines = splitTextToFit(doc, prefix, maxCompletionWidth);
      prefixLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, currentY + (index * 6), { align: 'center' });
      });
      currentY += prefixLines.length * 6 + 3; // new row, small gap before course part
    }
    if (coursePart) {
      doc.setTextColor(0, 0, 32); // black for course part (e.g. "A FORKLIFT TRAINING COURSE")
      const courseLines = splitTextToFit(doc, coursePart, maxCompletionWidth);
      courseLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, currentY + (index * 6), { align: 'center' });
      });
      currentY += (courseLines.length * 6) + 8;
    }

    // Add extra spacing for EFA certificate before date section
    if (certificate.courseName === 'Emergency First Aid') {
      currentY += 10; // Move "on the" date section down for EFA
    }

    // 7. Issue Date - moved here after completion statement
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal'); // Professional body text
    doc.setTextColor(0, 0, 32);
    
    // Multi-day booking: show all dates (any course)
    const hasMultipleDates = certificate.courseDates && certificate.courseDates.length > 0;
    
    // For Shunter & Signalers, skip "on the" since it's already in the completion statement
    if (!hasMultipleDates && !isShunterSignalers) {
      doc.text('on the', pageWidth / 2, currentY, { align: 'center' });
    }
    
    currentY += 5;
    doc.setTextColor(0, 146, 63); // Brand green for date
    doc.setFont('helvetica', 'bold'); // Professional bold for emphasis
    
    // Parse the course date(s) and format for display
    let issueDate: string;
    
    if (hasMultipleDates) {
      // Use multiple dates formatting
      issueDate = formatMultipleDates(certificate.courseDates!);
    } else {
      // Single date - use existing logic
      try {
        const dateStr = certificate.courseDate.toString().trim();
        const courseDate = parseDate(dateStr);
        
        if (isNaN(courseDate.getTime()) || courseDate.getFullYear() < 1900 || courseDate.getFullYear() > 2100) {
          throw new Error(`Invalid course date parsed: ${courseDate.toString()}`);
        }
        
        issueDate = courseDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Error parsing course date for display:', error);
        console.error('Original date string:', certificate.courseDate);
        
        // Emergency fallback: try direct ISO parsing
        try {
          const fallbackDate = new Date(certificate.courseDate);
          if (!isNaN(fallbackDate.getTime()) && fallbackDate.getFullYear() > 1900 && fallbackDate.getFullYear() < 2100) {
            issueDate = fallbackDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            });
          } else {
            issueDate = certificate.courseDate; // Last resort: use raw string
          }
        } catch {
          issueDate = certificate.courseDate; // Last resort: use raw string
        }
      }
    }
    
    // For multiple dates, add "COURSE DATES:" prefix and allow date line to wrap
    if (hasMultipleDates) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 32);
      doc.text('COURSE DATES:', pageWidth / 2, currentY, { align: 'center' });
      currentY += 5;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 146, 63);
    }

    // Wrap long date text (e.g. multiple training dates) so it fits the page
    const dateLines = splitTextToFit(doc, issueDate, contentWidth - 20);
    dateLines.forEach((line, idx) => {
      doc.text(line, pageWidth / 2, currentY + (idx * 5), { align: 'center' });
    });
    currentY += dateLines.length * 5;

    // Add dotted line under date
    currentY += 12;

    // 8. Company Name - moved here after issue date
    // Skip for Shunter & Signalers since company name is already in completion statement
    if (!isShunterSignalers) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal'); // Clean professional text
      doc.setTextColor(0, 0, 32); // Primary text for "on behalf of"
      doc.text('on behalf of', pageWidth / 2, currentY, { align: 'center' });

      currentY += 5;
      doc.setFontSize(14); // Increased font size for company name
      doc.setFont('helvetica', 'bold'); // Professional emphasis
      doc.setTextColor(0, 146, 63); // Brand green for company name
      doc.text(certificate.companyName, pageWidth / 2, currentY, { align: 'center' });

      // Add dotted line under company name
      // Tighter spacing for certificates with large images
      if (certificate.courseName === 'Abrasive Wheels') {
        currentY += 8; // Reduced space for Abrasive Wheels
      } else if (hasLargeImage) {
        currentY += 10; // Reduced space for certificates with large truck images
      } else {
        currentY += 15; // More space after larger company name
      }
    } else {
      // For Shunter & Signalers, render additional static text right after date
      const isShunterSignalers = certificate.courseName === 'Safety Awareness Training for Shunter & Signalers';
      if (isShunterSignalers && courseConfig.additionalStaticText) {
        currentY += 8; // Space before additional text
        
        doc.setFontSize(7); // Smaller font for Shunter & Signalers
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 32);
        
        const lineSpacing = 5; // Tighter spacing
        
        courseConfig.additionalStaticText.forEach((text, index) => {
          const textLines = splitTextToFit(doc, text, contentWidth - 10);
          textLines.forEach((line, lineIndex) => {
            doc.text(line, pageWidth / 2, currentY + (index * lineSpacing) + (lineIndex * 2.5), { align: 'center' });
          });
        });
        currentY += (courseConfig.additionalStaticText.length * lineSpacing) + 12; // Spacing after topics
      } else {
        // For other courses, add spacing after date
        currentY += 15;
      }
    }

    // 8a. Additional static text - render immediately after company name for Working at Height
    const isWorkingAtHeight = certificate.courseName.includes('Working at Height');
    if (isWorkingAtHeight && courseConfig.additionalStaticText) {
      currentY += 8; // Space before additional text

      doc.setFontSize(9); // Bigger font for Working at Height
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 32);

      const lineSpacing = 4;

      courseConfig.additionalStaticText.forEach((text, index) => {
        const textLines = splitTextToFit(doc, text, contentWidth - 10);
        textLines.forEach((line, lineIndex) => {
          doc.text(line, pageWidth / 2, currentY + (index * lineSpacing) + (lineIndex * 3), { align: 'center' });
        });
      });
      currentY += (courseConfig.additionalStaticText.length * lineSpacing) + 8;
    }

    // 9. Course-specific fields (Truck specifications, etc.) - moved after company name
    // All lift truck, forklift, and MEWP courses can show truck details if any field is provided
    const isTruckCourse = (
      certificate.courseName.includes('MEWP') ||
      certificate.courseName.includes('Lift Truck') ||
      certificate.courseName.includes('IPAF') ||
      certificate.courseName.includes('VNA') ||
      certificate.courseName.includes('Stacker') ||
      certificate.courseName.includes('Order Picker') ||
      certificate.courseName.includes('Pivot Steer') ||
      certificate.courseName.includes('Multidirectional') ||
      certificate.courseName.includes('Power Pallet Instructor Training') ||
      certificate.courseName.includes('Power Pallet Instructor') ||
      courseNameLower.includes('forklift')
    );

    // Check if any truck detail fields are provided
    const hasAnyTruckDetails = (
      certificate.truckType ||
      certificate.truckMake ||
      certificate.truckCapacity ||
      certificate.truckAttachments
    );

    const hasTruckDetails = isTruckCourse && hasAnyTruckDetails;
    
    // Add extra spacing for certificates without truck details
    if (!hasTruckDetails) {
      // Much less spacing for Abrasive Wheels
      if (certificate.courseName === 'Abrasive Wheels') {
        currentY += 5; // Minimal spacing for Abrasive Wheels
      } else if (hasLargeImage) {
        currentY += 12; // Less spacing for certificates with large images
      } else {
        currentY += 20; // Additional spacing for other certificates
      }
    }
    
    if (hasTruckDetails) {
      doc.setFontSize(9);

      // Build truck details array - only include fields that have values
      interface TruckDetail {
        label: string;
        value: string;
      }

      const truckDetails: TruckDetail[] = [];
      let attachmentsDetail: TruckDetail | null = null;

      // Only add fields that have actual values (not empty strings)
      // Separate Type, Make, Capacity from Attachments
      if (certificate.truckType && certificate.truckType.trim()) {
        // Use "TRUCK TYPE:" for MEWP courses, "TYPE:" for others
        const typeLabel = (certificate.courseName === 'MEWP Operator' || certificate.courseName === 'MEWP – Spider Lift Operator') ? 'TRUCK TYPE:' : 'TYPE:';
        truckDetails.push({ label: typeLabel, value: certificate.truckType.trim() });
      }

      if (certificate.truckMake && certificate.truckMake.trim()) {
        truckDetails.push({ label: 'MAKE:', value: certificate.truckMake.trim() });
      }

      if (certificate.truckCapacity && certificate.truckCapacity.trim()) {
        truckDetails.push({ label: 'CAPACITY:', value: certificate.truckCapacity.trim() });
      }

      // Store attachments separately to render on a separate line below
      if (certificate.truckAttachments && certificate.truckAttachments.trim()) {
        attachmentsDetail = { label: 'ATTACHMENTS:', value: certificate.truckAttachments.trim() };
      }

      // Only render if we have at least one non-empty field
      if (truckDetails.length > 0 || attachmentsDetail) {
        // Render Type, Make, Capacity in one row (if they exist)
        if (truckDetails.length > 0) {
          // Calculate width for Type/Make/Capacity row
          let typeMakeCapacityWidth = 0;
          truckDetails.forEach((detail, index) => {
            doc.setFont('helvetica', 'bold');
            const labelWidth = doc.getTextWidth(detail.label);
            const valueWidth = doc.getTextWidth(detail.value);
            const separatorWidth = index < truckDetails.length - 1 ? doc.getTextWidth(' • ') : 0;
            typeMakeCapacityWidth += labelWidth + valueWidth + separatorWidth + 1;
          });

          // Draw Type, Make, Capacity row
          let startX = (pageWidth - typeMakeCapacityWidth) / 2;
          truckDetails.forEach((detail, index) => {
            // Draw label in bold black
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 32);
            doc.text(detail.label, startX, currentY);
            startX += doc.getTextWidth(detail.label) + 1;

            // Draw value in bold green
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(9, 153, 85); // #099955
            doc.text(detail.value, startX, currentY);
            startX += doc.getTextWidth(detail.value);

            // Draw separator if not last item (using bullet separator)
            if (index < truckDetails.length - 1) {
              doc.setTextColor(0, 0, 32);
              doc.text(' • ', startX, currentY);
              startX += doc.getTextWidth(' • ');
            }
          });

          currentY += 5; // Small spacing before attachments row
        }

        // Render Attachments on a separate row below (if it exists)
        if (attachmentsDetail) {
          // Calculate width for attachments
          doc.setFont('helvetica', 'bold');
          const attachmentsLabelWidth = doc.getTextWidth(attachmentsDetail.label);
          const attachmentsValueWidth = doc.getTextWidth(attachmentsDetail.value);
          const attachmentsWidth = attachmentsLabelWidth + attachmentsValueWidth + 1;

          // Draw Attachments row
          let startX = (pageWidth - attachmentsWidth) / 2;
          
          // Draw label in bold black
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 32);
          doc.text(attachmentsDetail.label, startX, currentY);
          startX += attachmentsLabelWidth + 1;

          // Draw value in bold green
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(9, 153, 85); // #099955
          doc.text(attachmentsDetail.value, startX, currentY);

          currentY += hasLargeImage ? 6 : 8;
        } else if (truckDetails.length > 0) {
          // Only Type/Make/Capacity, no attachments - use normal spacing
          currentY += hasLargeImage ? 1 : 3;
        }
      } else {
        // No truck details to show, adjust spacing
        if (certificate.courseName === 'Abrasive Wheels') {
          currentY += 2;
        } else if (hasLargeImage) {
          currentY += 10;
        } else {
          currentY += 15;
        }
      }
    }

    // Add extra spacing after truck details section for certificates without truck details
    if (!hasTruckDetails) {
      // Reduce spacing for Abrasive Wheels to move content up
      if (certificate.courseName === 'Abrasive Wheels') {
        currentY += 2; // Minimal spacing for Abrasive Wheels
      } else if (hasLargeImage) {
        currentY += 10; // Reduced spacing for certificates with large images
      } else {
        currentY += 15; // Additional spacing for other certificates
      }
    }

    // 10. Additional static text (for courses like Abrasive Wheels - Working at Height and Shunter & Signalers handled earlier)
    if (courseConfig.additionalStaticText && !isWorkingAtHeight) {
      // Skip Shunter & Signalers as it's already rendered after the date section
      const isShunterSignalers = certificate.courseName === 'Safety Awareness Training for Shunter & Signalers';
      
      if (!isShunterSignalers) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal'); // Professional small text
        doc.setTextColor(0, 0, 32);

        const lineSpacing = 8;

        courseConfig.additionalStaticText.forEach((text, index) => {
          const textLines = splitTextToFit(doc, text, contentWidth - 10);
          textLines.forEach((line, lineIndex) => {
            doc.text(line, pageWidth / 2, currentY + (index * lineSpacing) + (lineIndex * 3), { align: 'center' });
          });
        });
        currentY += (courseConfig.additionalStaticText.length * lineSpacing) + 3;

        // Add extra spacing after additional static text for certificates without truck details
        if (!hasTruckDetails) {
          // Reduce spacing for Abrasive Wheels to move content up
          if (certificate.courseName === 'Abrasive Wheels') {
            currentY += 5; // Reduced spacing for Abrasive Wheels
          } else {
            currentY += 12; // Additional spacing for other certificates
          }
        }
      }
    }

    // 11. Instructor Section - moved before Valid Until for Abrasive Wheels
    if (certificate.courseName === 'Abrasive Wheels') {
      currentY += 5; // Reduced spacing before instructor

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 32);
      doc.setFont('helvetica', 'normal');

      const instructorText = 'INSTRUCTOR: ';
      const instructorWidth = doc.getTextWidth(instructorText);
      const instructorName = certificate.instructorName || 'Not Specified';
      const instructorValueWidth = doc.getTextWidth(instructorName);
      const instructorStartX = (pageWidth - (instructorWidth + instructorValueWidth)) / 2;

      doc.text(instructorText, instructorStartX, currentY);

      doc.setTextColor(0, 146, 63);
      doc.setFont('helvetica', 'bold');
      doc.text(instructorName, instructorStartX + instructorWidth, currentY);
      currentY += 12; // More spacing after instructor before Valid Until

      // Instructor Registration Number (if required)
      if (courseConfig?.requiredFields?.includes('instructorRegNumber') && certificate.instructorRegNumber) {
        doc.setTextColor(0, 0, 32);
        doc.setFont('helvetica', 'normal');
        const regText = 'INSTRUCTOR REG NUMBER: ';
        const regWidth = doc.getTextWidth(regText);
        const regValueWidth = doc.getTextWidth(certificate.instructorRegNumber);
        const regStartX = (pageWidth - (regWidth + regValueWidth)) / 2;

        doc.text(regText, regStartX, currentY);

        doc.setTextColor(0, 146, 63);
        doc.setFont('helvetica', 'bold');
        doc.text(certificate.instructorRegNumber, regStartX + regWidth, currentY);
        currentY += 10; // More spacing after reg number
      }
    }

    // 12. Valid Until Section (Bottom Right)
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 32); // Primary text for label
    doc.setFont('helvetica', 'normal'); // Professional text

    const validUntilText = 'VALID UNTIL: ';
    const validUntilWidth = doc.getTextWidth(validUntilText);
    const expiryDateWidth = doc.getTextWidth(formattedExpiryDate);
    const totalValidUntilWidth = validUntilWidth + expiryDateWidth;
    const validUntilStartX = (pageWidth - totalValidUntilWidth) / 2;

    // For Abrasive Wheels, use currentY position (instructor already rendered above)
    // For other courses, use bottomSectionY position
    const validUntilY = certificate.courseName === 'Abrasive Wheels' ? currentY : bottomSectionY + 15;

    doc.text(validUntilText, validUntilStartX, validUntilY);

    doc.setFont('helvetica', 'bold'); // Professional emphasis
    doc.setTextColor(185, 28, 28); // Red for expiry date only
    doc.text(formattedExpiryDate, validUntilStartX + validUntilWidth, validUntilY);

    // Update currentY after Valid Until for Abrasive Wheels
    if (certificate.courseName === 'Abrasive Wheels') {
      currentY += 8;
    }

    // 11. Compliance Statement - with proper text wrapping to prevent overflow
    // Skip for Working at Height as it's now part of additionalStaticText
    if (courseConfig.complianceStatement && !isWorkingAtHeight) {
      // Check if this is a forklift-type course that needs truck details
      const isForkliftCourse = certificate.courseName.includes('MEWP') || 
                              certificate.courseName.includes('Lift Truck') ||
                              certificate.courseName.includes('IPAF') ||
                              certificate.courseName.includes('VNA') ||
                              certificate.courseName.includes('Forklift');
      
      if (isForkliftCourse) {
        // Forklift courses: keep original spacing and font size
        doc.setFontSize(9);
        currentY += 12; // Original spacing for forklift courses
      } else if (certificate.courseName === 'Manual Handling') {
        // Manual Handling: move up more and increase font size significantly
        doc.setFontSize(11); // Bigger font for Manual Handling
        currentY -= 5; // Move up more (negative value moves it up)
      } else if (certificate.courseName === 'Emergency First Aid') {
        // Emergency First Aid: move up significantly to fill empty space
        doc.setFontSize(11); // Bigger font for EFA
        currentY -= 25; // Move up much more to fill the empty space
      } else if (certificate.courseName === 'Chemical Safety & Spills Response Training') {
        // Chemical Safety: move compliance statement up much more
        doc.setFontSize(11); // Same font size as Manual Handling
        currentY -= 30; // Move up much more to avoid instructor overlap
      } else {
        // Other non-forklift courses: move up and increase font size slightly
        doc.setFontSize(10); // Slightly bigger font
        currentY += 2; // Move up more (reduced spacing)
      }
      
      // Set font style based on course type
      if (certificate.courseName === 'Chemical Safety & Spills Response Training') {
        doc.setFont('helvetica', 'bold'); // Bold for Chemical Safety
        doc.setTextColor(0, 0, 32);
        
        // For Chemical Safety: split at \n character to handle the newline properly
        const fullStatement = courseConfig.complianceStatement;
        const newlineIndex = fullStatement.indexOf('\n');
        
        if (newlineIndex !== -1) {
          const firstPart = fullStatement.substring(0, newlineIndex);
          const secondPart = fullStatement.substring(newlineIndex + 1);
          
          // Render first part with wider width and more line spacing
          const firstLines = splitTextToFit(doc, firstPart, contentWidth - 10);
          firstLines.forEach((line, index) => {
            doc.text(line, pageWidth / 2, currentY + (index * 4), { align: 'center' });
          });
          currentY += (firstLines.length * 4) + 8; // More space between parts
          
          // Render second part with wider width and more line spacing
          const secondLines = splitTextToFit(doc, secondPart, contentWidth - 10);
          secondLines.forEach((line, index) => {
            doc.text(line, pageWidth / 2, currentY + (index * 4), { align: 'center' });
          });
          currentY += (secondLines.length * 4);
        } else {
          // Fallback: use normal text wrapping
          const complianceLines = splitTextToFit(doc, fullStatement, contentWidth - 25);
          complianceLines.forEach((line, index) => {
            doc.text(line, pageWidth / 2, currentY + (index * 3.5), { align: 'center' });
          });
          currentY += (complianceLines.length * 3.5);
        }
        currentY += 8; // Add space after Chemical Safety compliance statement
      } else {
        doc.setFont('helvetica', 'normal'); // Normal for other courses
      doc.setTextColor(0, 0, 32);
      
      // Split compliance statement to fit within margins
      const maxLines = 8; // Limit lines to prevent footer overlap
      
      const complianceLines = splitTextToFit(doc, courseConfig.complianceStatement, contentWidth - 25);
      const linesToShow = complianceLines.slice(0, maxLines);
      
      linesToShow.forEach((line, index) => {
        doc.text(line, pageWidth / 2, currentY + (index * 3.5), { align: 'center' });
        });
        currentY += (linesToShow.length * 3.5) + 8; // Add space after other compliance statements
      }
    }

    // Add extra space before instructor section for Chemical Safety
    if (certificate.courseName === 'Chemical Safety & Spills Response Training') {
      currentY += 15; // Extra space to move instructor down
    }

    // Define footer position early for layout calculations
    const footerY = pageHeight - 25; // Footer position

    // 12. Instructor Section - moved after compliance statement
    // Skip for Abrasive Wheels as it's already rendered above
    if (certificate.courseName !== 'Abrasive Wheels') {
      // Check if we're too close to the footer - leave space for footer
      const minFooterSpace = 35; // Minimum space needed for footer
      const availableSpace = footerY - currentY;

      // If too close to footer, move instructor section up
      if (availableSpace < minFooterSpace) {
        currentY = footerY - minFooterSpace;
      }

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 32); // Primary text for label
      doc.setFont('helvetica', 'normal'); // Professional text

      // Use instructorLabelOverride from config if available, otherwise default to "INSTRUCTOR:"
      const instructorText = (courseConfig as any)?.instructorLabelOverride || 'INSTRUCTOR: ';
      const instructorWidth = doc.getTextWidth(instructorText);
      const instructorName = certificate.instructorName || 'Not Specified';
      const instructorValueWidth = doc.getTextWidth(instructorName);
      const instructorStartX = (pageWidth - (instructorWidth + instructorValueWidth)) / 2;

      doc.text(instructorText, instructorStartX, currentY);

      // Dynamic instructor name in brand green
      doc.setTextColor(0, 146, 63); // Brand green for dynamic content
      doc.setFont('helvetica', 'bold'); // Make instructor name bold
      doc.text(instructorName, instructorStartX + instructorWidth, currentY);
      currentY += 6;

      // Instructor Registration Number (if required by course)
      if (courseConfig?.requiredFields?.includes('instructorRegNumber') && certificate.instructorRegNumber) {
        doc.setTextColor(0, 0, 32); // Primary text for label
        doc.setFont('helvetica', 'normal'); // Reset to normal for label
        // Use "REG NUMBER:" for MEWP, "INSTRUCTOR REG NUMBER:" for others
        const regText = (certificate.courseName === 'MEWP Operator' || certificate.courseName === 'MEWP – Spider Lift Operator') ? 'REG NUMBER: ' : 'INSTRUCTOR REG NUMBER: ';
        const regWidth = doc.getTextWidth(regText);
        const regValueWidth = doc.getTextWidth(certificate.instructorRegNumber);
        const regStartX = (pageWidth - (regWidth + regValueWidth)) / 2;

        doc.text(regText, regStartX, currentY);

        // Dynamic reg number in brand green
        doc.setTextColor(0, 146, 63); // Brand green
        doc.setFont('helvetica', 'bold'); // Make instructor reg number bold
        doc.text(certificate.instructorRegNumber, regStartX + regWidth, currentY);
        currentY += 6;
      }
    }
    // 14. Footer (Bottom Center) - Contact information and certificate ID
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0); // Black color
    doc.setFont('helvetica', 'bold'); // Bold text
    
    // Contact information
    doc.text('Ireland T&T', pageWidth / 2, footerY, { align: 'center' });
    doc.text('Office 15, Unit 8, Dunshaughlin Business Park, Dunshaughlin, Co. Meath, A85 EE98', pageWidth / 2, footerY + 3, { align: 'center' });
    doc.text('E-MAIL: info@irelandtt.com | Mobile: 085 8244 628 | Web: www.irelandtt.com', pageWidth / 2, footerY + 6, { align: 'center' });
    
    // Certificate ID - only use certificate.id if it is already TT-XXXXXX; otherwise generate a new unique one
    let certNumber: string;
    if (certificate.id && certificate.id.trim() && certificate.id.startsWith('TT-')) {
      certNumber = certificate.id;
    } else {
      certNumber = await getCertificateNumber();
    }
    doc.setFontSize(6);
    doc.text(`Certificate ID: ${certNumber}`, pageWidth / 2, footerY + 12, { align: 'center' });

    return doc.output('blob');

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`Failed to generate PDF for ${certificate.attendeeName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const downloadCertificatePDF = async (certificate: ProcessedCertificate): Promise<void> => {
  const blob = await generateCertificatePDF(certificate);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = certificate.fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const generateAttendanceListPDF = async (
  certificates: ProcessedCertificate[],
  companyName: string,
  courseDate: string
): Promise<Blob> => {
  try {
    const filteredCertificates = certificates.filter(
      cert => cert.companyName === companyName && cert.courseDate === courseDate
    );

    const formatDateToDDMMYYYY = (dateStr: string): string => {
      try {
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch {
        return dateStr;
      }
    };

    const attendanceData = filteredCertificates.map(cert => ({
      'Attendee Name': cert.attendeeName || '',
      'Course Name': cert.courseName || '',
      'Company Name': cert.companyName || '',
      'Instructor Name': cert.instructorName || '',
      'Course Date': formatDateToDDMMYYYY(cert.courseDate) || ''
    }));

    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(24);
    doc.setTextColor(0, 154, 77);
    doc.text('Ireland T&T', pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(16);
    doc.setTextColor(55, 65, 81);
    doc.text('Course Attendance List', pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 38, { align: 'center' });

    try {
      const logoPath = resolveImagePath('https://irelandtt.com/img/Irelandtt-logo-transparent.png');
      const logoResponse = await fetch(logoPath);
      const logoBlob = await logoResponse.blob();
      
      // Compress and resize logo to reduce PDF size while preserving transparency
      const logoDataUrl = await new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to resize and compress the image
          const canvas = document.createElement('canvas');
          const maxWidth = 200; // Max width for logo in pixels
          const maxHeight = 60; // Max height for logo in pixels
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d', { alpha: true });
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          // Clear canvas with transparent background
          ctx.clearRect(0, 0, width, height);
          
          // Draw resized image (preserves transparency)
          ctx.drawImage(img, 0, 0, width, height);
          
          // Keep PNG format to preserve transparency (JPEG doesn't support transparency)
          const compressedDataUrl = canvas.toDataURL('image/png', 0.9); // 90% quality PNG
          resolve(compressedDataUrl);
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(logoBlob);
      });

      doc.addImage(logoDataUrl, 'PNG', pageWidth - 50, 5, 40, 15);
    } catch (logoError) {
      console.warn('Could not load logo:', logoError);
      doc.setFillColor(30, 64, 175);
      doc.rect(pageWidth - 50, 5, 40, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text('LOGO', pageWidth - 30, 13, { align: 'center' });
    }

    const startY = 50;
    const tableWidth = pageWidth - 20;
    const colWidths = [30, 35, 50, 30, 25]; // Increased Company Name from 35 to 50, adjusted others
    const rowHeight = 8;

    doc.setFillColor(243, 244, 246);
    doc.rect(10, startY, tableWidth, rowHeight, 'F');

    doc.setTextColor(55, 65, 81);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    let currentX = 10;
    const headers = ['Attendee Name', 'Course Name', 'Company Name', 'Instructor Name', 'Course Date'];
    headers.forEach((header, index) => {
      doc.text(header, currentX + 2, startY + 5);
      currentX += colWidths[index];
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    let currentY = startY + rowHeight;
    attendanceData.forEach((row, index) => {
      // Calculate row height based on longest cell content (especially company name)
      const rowData = [row['Attendee Name'], row['Course Name'], row['Company Name'], row['Instructor Name'], row['Course Date']];
      let maxLines = 1;
      
      rowData.forEach((cellData, cellIndex) => {
        const cellWidth = colWidths[cellIndex] - 4; // Account for padding
        const lines = splitTextToFit(doc, cellData, cellWidth);
        if (lines.length > maxLines) {
          maxLines = lines.length;
        }
      });
      
      const dynamicRowHeight = rowHeight * maxLines;
      
      if (index % 2 === 0) {
        doc.setFillColor(249, 250, 251);
        doc.rect(10, currentY, tableWidth, dynamicRowHeight, 'F');
      }

      doc.setDrawColor(209, 213, 219);
      doc.rect(10, currentY, tableWidth, dynamicRowHeight);

      currentX = 10;
      rowData.forEach((cellData, cellIndex) => {
        const cellWidth = colWidths[cellIndex] - 4; // Account for padding
        const lines = splitTextToFit(doc, cellData, cellWidth);
        
        lines.forEach((line, lineIndex) => {
          doc.text(line, currentX + 2, currentY + 5 + (lineIndex * rowHeight));
        });
        
        currentX += colWidths[cellIndex];
      });

      currentY += dynamicRowHeight;

      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = 20;
      }
    });

    const footerY = pageHeight - 15;
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('Ireland T&T | Email: info@irelandtt.com | Phone: 085 8244 628', pageWidth / 2, footerY, { align: 'center' });
    doc.text('Website: www.irelandtt.com', pageWidth / 2, footerY + 5, { align: 'center' });

    // Use compression options to reduce file size
    return doc.output('blob', { 
      compress: true 
    });
  } catch (error) {
    console.error('Error generating attendance list PDF:', error);
    throw new Error(`Failed to generate attendance list: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const downloadAttendanceListPDF = async (
  certificates: ProcessedCertificate[],
  companyName: string,
  courseDate: string
): Promise<void> => {
  const blob = await generateAttendanceListPDF(certificates, companyName, courseDate);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const filteredCerts = certificates.filter(
    cert => cert.companyName === companyName && cert.courseDate === courseDate
  );

  const uniqueCourses = [...new Set(filteredCerts.map(cert => cert.courseName))];
  const courseName = uniqueCourses.length === 1 ? uniqueCourses[0] :
                     uniqueCourses.length > 1 ? `${uniqueCourses.length}-courses` : 'course';

  const formatDateToMMDDYYYY = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}${day}${year}`;
    } catch {
      return new Date().toISOString().split('T')[0].replace(/-/g, '');
    }
  };

  const cleanCourseName = courseName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
  const cleanCompanyName = companyName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
  const formattedDate = formatDateToMMDDYYYY(courseDate);

  link.download = `${cleanCourseName}-${cleanCompanyName}-${formattedDate}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};